import { z, type ZodType } from 'zod'

import { isDev } from '@/core/config/env'
import { getAbortReason } from '@/core/lib/abort'
import { createDeadline } from '@/core/lib/deadline'
import {
  createTelemetryEmitter,
  type TelemetrySink,
} from '@/core/lib/telemetry'
import { normalizeRequestId } from '@/core/utils/request-id'

import { logDevFetchAttempt } from './dev-log'
import {
  STANDARD_MUTATION,
  STANDARD_READ,
  type FetchErrorCategory,
  type MutationCapability,
  type RequestPolicy,
} from './request-policy'

export type HttpMethod = 'GET' | 'POST' | 'PUT' | 'DELETE' | 'PATCH'
const MAX_REQUEST_ATTEMPTS = 5
const MAX_RESPONSE_BYTES = 4_194_304

// DRF/Django 오류는 `{error, code}` 형태로 온다 — message·detail 이 없으면 error·code 를 대신 쓴다
const failureEnvelopeSchema = z.object({
  code: z.string().optional(),
  detail: z.string().optional(),
  message: z.string().optional(),
  error: z.string().optional(),
})

type FetchMetadata = {
  readonly attemptCount: number
  readonly elapsedMs: number
  readonly policyId: string
  readonly status: number
  readonly correlationId?: string
}

export type FetcherOptions<TBody = unknown, TResponse = unknown> = {
  body?: TBody
  credentials?: RequestCredentials
  headers?: Readonly<Record<string, string>>
  signal?: AbortSignal
  timeoutMs?: number
  responseSchema?: ZodType<TResponse>
  requestPolicy?: RequestPolicy
  idempotencyKey?: string
  mutationCapabilities?: Readonly<Partial<Record<MutationCapability, boolean>>>
  telemetry?: TelemetrySink
}

type FetcherError = {
  message: string
  detail: string
  category: FetchErrorCategory
  retryable: boolean
  unknownOutcome: boolean
  /** 공개 API의 구조화된 오류를 도메인 경계에서 검증할 때만 사용한다. */
  body?: unknown
}

export type FetcherSuccessResponse<T> = {
  ok: true
  message: string
  data: T
  meta: FetchMetadata
}

export type FetcherErrorResponse = {
  ok: false
  status: number
  message: string
  data: FetcherError | null
  meta: FetchMetadata
}

export type FetcherResponse<T> =
  FetcherSuccessResponse<T> | FetcherErrorResponse

export class FetchError extends Error {
  readonly category: FetchErrorCategory
  readonly status: number
  readonly detail: string
  readonly retryable: boolean
  readonly unknownOutcome: boolean
  readonly meta: FetchMetadata

  constructor(response: FetcherErrorResponse) {
    super(response.message)
    this.name = 'FetchError'
    this.category = response.data?.category ?? 'unknown'
    this.status = response.status
    this.detail = response.data?.detail ?? 'UNKNOWN'
    this.retryable = response.data?.retryable ?? false
    this.unknownOutcome = response.data?.unknownOutcome ?? false
    this.meta = response.meta
  }
}

type FetchParams<TResponse> = {
  method: HttpMethod
  url: string
  baseUrl?: string
  headers: Record<string, string>
  body?: string | FormData
  credentials?: RequestCredentials
  signal?: AbortSignal
  timeoutMs?: number
  responseSchema?: ZodType<TResponse>
  requestPolicy?: RequestPolicy
  idempotencyKey?: string
  mutationCapabilities?: Readonly<Partial<Record<MutationCapability, boolean>>>
  telemetry?: TelemetrySink
  context?: 'server' | 'client'
  /** 서버 adapter만 주입한다. Next 제어 흐름을 API 실패나 재시도로 바꾸지 않는다. */
  rethrowFrameworkError?: (error: unknown) => void
  cache?: RequestCache
  next?: { revalidate?: number | false; tags?: string[] }
}

type NextFetchInit = RequestInit & {
  next?: { revalidate?: number | false; tags?: string[] }
}

type AttemptResult<T> =
  | { readonly response: FetcherSuccessResponse<T> }
  | { readonly response: FetcherErrorResponse }

class ResponseTooLargeError extends Error {}

export async function executeFetch<TResponse>(
  params: FetchParams<TResponse>
): Promise<FetcherResponse<TResponse>> {
  const startedAt = Date.now()
  const policy =
    params.requestPolicy ??
    (params.method === 'GET' ? STANDARD_READ : STANDARD_MUTATION)
  const configuredDeadline = params.timeoutMs ?? policy.totalDeadlineMs
  const totalDeadlineMs = Math.min(configuredDeadline, policy.totalDeadlineMs)
  const correlationId = getCorrelationId(params.headers)
  const emitter = params.telemetry
    ? createTelemetryEmitter(params.telemetry)
    : undefined
  let apiUrl: string

  try {
    apiUrl = resolveApiUrl(
      params.url,
      params.context ?? 'server',
      params.baseUrl
    )
    assertRequestConfiguration(params, policy)
  } catch {
    return createFailure({
      category: 'configuration_error',
      status: 0,
      message: 'The request configuration is invalid.',
      detail: 'CONFIGURATION_ERROR',
      retryable: false,
      unknownOutcome: false,
      attemptCount: 0,
      startedAt,
      policy,
      correlationId,
    })
  }

  const totalDeadline = createDeadline({
    timeoutMs: totalDeadlineMs,
    signal: params.signal,
  })
  let attemptCount = 0

  emitter?.emit('request.started', {
    attributes: { method: params.method, policyId: policy.id },
  })

  try {
    while (attemptCount < policy.maxAttempts) {
      if (totalDeadline.signal.aborted) {
        const failure = createAbortFailure(
          totalDeadline.signal,
          attemptCount,
          startedAt,
          policy,
          correlationId,
          params.method !== 'GET' && attemptCount > 0
        )
        emitCompletion(emitter, failure, params.method)
        return failure
      }

      if (Date.now() >= totalDeadline.deadlineAtMs) {
        const failure = createDeadlineFailure(
          attemptCount,
          startedAt,
          policy,
          correlationId,
          params.method !== 'GET' && attemptCount > 0
        )
        emitCompletion(emitter, failure, params.method)
        return failure
      }

      attemptCount += 1
      emitter?.emit('request.attempt', {
        attributes: {
          attempt: attemptCount,
          method: params.method,
          policyId: policy.id,
        },
      })

      const remainingMs = totalDeadline.deadlineAtMs - Date.now()

      if (remainingMs <= 0) {
        const failure = createDeadlineFailure(
          attemptCount,
          startedAt,
          policy,
          correlationId,
          params.method !== 'GET' && attemptCount > 0
        )
        emitCompletion(emitter, failure, params.method)
        return failure
      }

      const attemptDeadline = createDeadline({
        timeoutMs: Math.min(policy.attemptTimeoutMs, remainingMs),
        signal: totalDeadline.signal,
      })
      let result: AttemptResult<TResponse>

      try {
        result = await executeAttempt(params, apiUrl, attemptDeadline.signal, {
          attemptCount,
          startedAt,
          policy,
          correlationId,
          deadlineAtMs: totalDeadline.deadlineAtMs,
        })
      } catch (error) {
        params.rethrowFrameworkError?.(error)
        result = {
          response: createFailure({
            category: 'unknown',
            status: 0,
            message: 'The request result is unknown.',
            detail: 'UNKNOWN',
            retryable: false,
            unknownOutcome: params.method !== 'GET',
            attemptCount,
            startedAt,
            policy,
            correlationId,
          }),
        }
      } finally {
        attemptDeadline.dispose()
      }

      if (result.response.ok) {
        emitCompletion(emitter, result.response, params.method)
        return result.response
      }

      const shouldRetry = canRetry(
        result.response,
        params.method,
        policy,
        params.idempotencyKey,
        attemptCount
      )

      if (!shouldRetry) {
        emitCompletion(emitter, result.response, params.method)
        return result.response
      }

      const remainingAfterAttempt = totalDeadline.deadlineAtMs - Date.now()

      if (remainingAfterAttempt <= 0) {
        const failure = createDeadlineFailure(
          attemptCount,
          startedAt,
          policy,
          correlationId,
          params.method !== 'GET'
        )
        emitCompletion(emitter, failure, params.method)
        return failure
      }

      const sleepMs = Math.min(
        getFullJitterDelay(policy, attemptCount),
        Math.max(0, remainingAfterAttempt - 1)
      )

      if (sleepMs > 0 && !(await sleepUntil(sleepMs, totalDeadline.signal))) {
        const failure = createAbortFailure(
          totalDeadline.signal,
          attemptCount,
          startedAt,
          policy,
          correlationId,
          params.method !== 'GET'
        )
        emitCompletion(emitter, failure, params.method)
        return failure
      }
    }

    const failure = createFailure({
      category: 'unknown',
      status: 0,
      message: 'The request stopped without a terminal result.',
      detail: 'UNKNOWN',
      retryable: false,
      unknownOutcome: params.method !== 'GET' && attemptCount > 0,
      attemptCount,
      startedAt,
      policy,
      correlationId,
    })
    emitCompletion(emitter, failure, params.method)
    return failure
  } finally {
    totalDeadline.dispose()
  }
}

async function executeAttempt<TResponse>(
  params: FetchParams<TResponse>,
  apiUrl: string,
  signal: AbortSignal,
  metadata: {
    readonly attemptCount: number
    readonly startedAt: number
    readonly policy: RequestPolicy
    readonly correlationId?: string
    readonly deadlineAtMs: number
  }
): Promise<AttemptResult<TResponse>> {
  const init: NextFetchInit = {
    method: params.method,
    credentials: params.credentials,
    headers: withIdempotencyHeader(params.headers, params.idempotencyKey),
    body: params.body,
    signal,
  }

  if (params.cache !== undefined) init.cache = params.cache
  if ((params.context ?? 'server') === 'server' && params.next)
    init.next = params.next

  let response: Response | undefined
  let data: unknown = null
  let rawResponseBody: unknown = null
  let dispatched = false
  const finish = (result: AttemptResult<TResponse>, error?: unknown) => {
    if (isDev) {
      logDevFetchAttempt({
        apiUrl,
        attempt: metadata.attemptCount,
        body: params.body,
        context: params.context ?? 'server',
        elapsedMs: Math.max(0, Date.now() - metadata.startedAt),
        error,
        ...(!result.response.ok && result.response.data
          ? {
              failure: {
                category: result.response.data.category,
                detail: result.response.data.detail,
                retryable: result.response.data.retryable,
                unknownOutcome: result.response.data.unknownOutcome,
              },
            }
          : {}),
        headers: init.headers as Record<string, string>,
        method: params.method,
        response,
        responseBody: rawResponseBody,
        responseStatus: result.response.meta.status,
      })
    }
    return result
  }

  try {
    if (signal.aborted) {
      return finish({
        response: createAbortFailure(
          signal,
          metadata.attemptCount - 1,
          metadata.startedAt,
          metadata.policy,
          metadata.correlationId,
          false
        ),
      })
    }

    if (Date.now() >= metadata.deadlineAtMs) {
      return finish({
        response: createDeadlineFailure(
          metadata.attemptCount - 1,
          metadata.startedAt,
          metadata.policy,
          metadata.correlationId,
          false
        ),
      })
    }

    const responsePromise = fetch(apiUrl, init)
    dispatched = true
    response = await responsePromise
  } catch (error) {
    params.rethrowFrameworkError?.(error)
    if (signal.aborted) {
      return finish(
        {
          response: createAbortFailure(
            signal,
            metadata.attemptCount,
            metadata.startedAt,
            metadata.policy,
            metadata.correlationId,
            params.method !== 'GET' && dispatched
          ),
        },
        error
      )
    }

    return finish(
      {
        response: createFailure({
          category: 'network_error',
          status: 0,
          message: 'The network request failed.',
          detail: 'NETWORK_ERROR',
          retryable: true,
          unknownOutcome: params.method !== 'GET' && dispatched,
          ...metadata,
        }),
      },
      error
    )
  }

  try {
    data = await parseResponseBody(response, metadata.policy.maxResponseBytes)
    rawResponseBody = data
  } catch (error) {
    params.rethrowFrameworkError?.(error)
    if (signal.aborted) {
      return finish(
        {
          response: createAbortFailure(
            signal,
            metadata.attemptCount,
            metadata.startedAt,
            metadata.policy,
            metadata.correlationId,
            params.method !== 'GET'
          ),
        },
        error
      )
    }

    return finish(
      {
        response: createFailure({
          category:
            error instanceof ResponseTooLargeError
              ? 'schema_error'
              : 'network_error',
          status: 502,
          message: 'The API response could not be read safely.',
          detail:
            error instanceof ResponseTooLargeError
              ? 'RESPONSE_TOO_LARGE'
              : 'RESPONSE_READ_FAILED',
          retryable: !(error instanceof ResponseTooLargeError),
          unknownOutcome: params.method !== 'GET',
          ...metadata,
        }),
      },
      error
    )
  }

  if (!response.ok) {
    const category = classifyHttpFailure(response.status, data)
    return finish({
      response: createFailure({
        category,
        status: response.status,
        message: getErrorMessage(data, response.statusText),
        detail: getErrorDetail(data, response.status),
        retryable: metadata.policy.retryableStatuses.includes(response.status),
        unknownOutcome:
          params.method !== 'GET' &&
          (response.status === 408 || response.status >= 500),
        body: data,
        ...metadata,
      }),
    })
  }

  if (params.responseSchema) {
    try {
      const parsed = await params.responseSchema.safeParseAsync(data)

      if (!parsed.success) {
        return finish({
          response: createFailure({
            category: 'schema_error',
            status: 502,
            message: 'The API response did not match the expected schema.',
            detail: 'INVALID_RESPONSE',
            retryable: false,
            unknownOutcome: params.method !== 'GET',
            ...metadata,
          }),
        })
      }

      data = parsed.data
    } catch (error) {
      params.rethrowFrameworkError?.(error)
      return finish(
        {
          response: createFailure({
            category: 'schema_error',
            status: 502,
            message: 'The API response schema could not be evaluated.',
            detail: 'SCHEMA_EVALUATION_FAILED',
            retryable: false,
            unknownOutcome: params.method !== 'GET',
            ...metadata,
          }),
        },
        error
      )
    }
  }

  return finish({
    response: {
      ok: true,
      message: 'Success',
      data: data as TResponse,
      meta: createMetadata({ ...metadata, status: response.status }),
    },
  })
}

function createAbortFailure(
  signal: AbortSignal,
  attemptCount: number,
  startedAt: number,
  policy: RequestPolicy,
  correlationId: string | undefined,
  dispatchedMutation: boolean
): FetcherErrorResponse {
  const reason = getAbortReason(signal)
  const deadlineExceeded = reason?.code === 'deadline-exceeded'

  return createFailure({
    category: deadlineExceeded ? 'deadline_exceeded' : 'cancelled',
    status: 0,
    message: deadlineExceeded
      ? 'The request deadline was exceeded.'
      : 'The request was cancelled.',
    detail: deadlineExceeded ? 'DEADLINE_EXCEEDED' : 'CANCELLED',
    retryable: deadlineExceeded,
    unknownOutcome: dispatchedMutation,
    attemptCount,
    startedAt,
    policy,
    correlationId,
  })
}

function createFailure(input: {
  readonly category: FetchErrorCategory
  readonly status: number
  readonly message: string
  readonly detail: string
  readonly retryable: boolean
  readonly unknownOutcome: boolean
  readonly attemptCount: number
  readonly startedAt: number
  readonly policy: RequestPolicy
  readonly correlationId?: string
  readonly body?: unknown
}): FetcherErrorResponse {
  return {
    ok: false,
    status: input.status,
    message: input.message,
    data: {
      message: input.message,
      detail: input.detail,
      category: input.category,
      retryable: input.retryable,
      unknownOutcome: input.unknownOutcome,
      ...(input.body !== undefined ? { body: input.body } : {}),
    },
    meta: createMetadata(input),
  }
}

export function createConfigurationFailure<TResponse>(
  method: HttpMethod,
  requestPolicy?: RequestPolicy,
  options: {
    readonly message?: string
    readonly detail?: string
  } = {}
): FetcherResponse<TResponse> {
  const policy =
    requestPolicy ?? (method === 'GET' ? STANDARD_READ : STANDARD_MUTATION)

  return createFailure({
    category: 'configuration_error',
    status: 0,
    message: options.message ?? 'The request could not be prepared safely.',
    detail: options.detail ?? 'CONFIGURATION_ERROR',
    retryable: false,
    unknownOutcome: false,
    attemptCount: 0,
    startedAt: Date.now(),
    policy,
  })
}

function createDeadlineFailure(
  attemptCount: number,
  startedAt: number,
  policy: RequestPolicy,
  correlationId: string | undefined,
  dispatchedMutation: boolean
): FetcherErrorResponse {
  return createFailure({
    category: 'deadline_exceeded',
    status: 0,
    message: 'The request deadline was exceeded.',
    detail: 'DEADLINE_EXCEEDED',
    retryable: false,
    unknownOutcome: dispatchedMutation,
    attemptCount,
    startedAt,
    policy,
    correlationId,
  })
}

function createMetadata(input: {
  readonly attemptCount: number
  readonly startedAt: number
  readonly policy: RequestPolicy
  readonly correlationId?: string
  readonly status?: number
}): FetchMetadata {
  return {
    attemptCount: input.attemptCount,
    elapsedMs: Math.max(0, Date.now() - input.startedAt),
    policyId: input.policy.id,
    status: input.status ?? 0,
    ...(input.correlationId ? { correlationId: input.correlationId } : {}),
  }
}

function canRetry(
  response: FetcherErrorResponse,
  method: HttpMethod,
  policy: RequestPolicy,
  idempotencyKey: string | undefined,
  attemptCount: number
): boolean {
  if (attemptCount >= policy.maxAttempts || response.data === null) return false
  if (!policy.retryableMethods.includes(method)) return false
  if (
    method !== 'GET' &&
    (policy.idempotency !== 'required' || !idempotencyKey)
  ) {
    return false
  }

  return (
    policy.retryableCategories.includes(response.data.category) ||
    policy.retryableStatuses.includes(response.status)
  )
}

function assertRequestConfiguration<T>(
  params: FetchParams<T>,
  policy: RequestPolicy
): void {
  if (
    !Number.isSafeInteger(policy.maxAttempts) ||
    policy.maxAttempts < 1 ||
    policy.maxAttempts > MAX_REQUEST_ATTEMPTS
  ) {
    throw new Error('Request policy maxAttempts must be a positive integer.')
  }

  const boundedDurations = [
    policy.totalDeadlineMs,
    policy.attemptTimeoutMs,
    policy.backoffBaseMs,
    policy.maxBackoffMs,
    policy.maxResponseBytes,
  ]

  if (
    boundedDurations.some(
      (value) =>
        !Number.isSafeInteger(value) || value < 0 || value > 2_147_483_647
    ) ||
    policy.totalDeadlineMs === 0 ||
    policy.attemptTimeoutMs === 0 ||
    policy.maxResponseBytes === 0 ||
    policy.maxResponseBytes > MAX_RESPONSE_BYTES
  ) {
    throw new Error('Request policy durations are outside the supported range.')
  }

  if (
    !/^[a-z0-9][a-z0-9.-]{0,63}$/.test(policy.id) ||
    policy.retryableStatuses.length > 32 ||
    policy.retryableStatuses.some(
      (status) => !Number.isInteger(status) || status < 100 || status > 599
    )
  ) {
    throw new Error('Request policy identity or status bounds are invalid.')
  }

  if (
    params.timeoutMs !== undefined &&
    (!Number.isSafeInteger(params.timeoutMs) ||
      params.timeoutMs <= 0 ||
      params.timeoutMs > 2_147_483_647)
  ) {
    throw new Error('timeoutMs must be a finite positive number.')
  }

  if (params.method === 'GET' && policy.kind !== 'read') {
    throw new Error('GET requests require a read policy.')
  }

  if (params.method !== 'GET' && policy.kind !== 'mutation') {
    throw new Error('Mutation requests require a mutation policy.')
  }

  if (
    policy.idempotency === 'required' &&
    !isValidIdempotencyKey(params.idempotencyKey)
  ) {
    throw new Error(`Policy ${policy.id} requires an idempotency key.`)
  }

  if (
    policy.idempotency === 'forbidden' &&
    params.idempotencyKey !== undefined
  ) {
    throw new Error(`Policy ${policy.id} forbids an idempotency key.`)
  }

  for (const capability of policy.requiredCapabilities) {
    if (params.mutationCapabilities?.[capability] !== true) {
      throw new Error(
        `Policy ${policy.id} requires the ${capability} capability.`
      )
    }
  }

  const headerKey = Object.entries(params.headers).find(
    ([key]) => key.toLowerCase() === 'idempotency-key'
  )?.[1]

  if (headerKey !== undefined && headerKey !== params.idempotencyKey) {
    throw new Error(
      'Idempotency-Key must be supplied through idempotencyKey only.'
    )
  }
}

function isValidIdempotencyKey(value: string | undefined): boolean {
  return value !== undefined && /^[A-Za-z0-9._:-]{8,128}$/.test(value)
}

function withIdempotencyHeader(
  headers: Readonly<Record<string, string>>,
  idempotencyKey: string | undefined
): Record<string, string> {
  if (!idempotencyKey) return { ...headers }

  const withoutIdempotencyKey = Object.fromEntries(
    Object.entries(headers).filter(
      ([key]) => key.toLowerCase() !== 'idempotency-key'
    )
  )

  return { ...withoutIdempotencyKey, 'Idempotency-Key': idempotencyKey }
}

function resolveApiUrl(
  url: string,
  context: 'server' | 'client',
  baseUrl?: string
): string {
  const path = url.startsWith('/') ? url : `/${url}`

  if (context === 'client') return `/api${path}`
  if (!baseUrl) throw new Error('Server requests require a baseUrl.')

  return `${baseUrl.replace(/\/+$/, '')}/api${path}`
}

async function parseResponseBody(
  response: Response,
  maxResponseBytes: number
): Promise<unknown> {
  if (response.status === 204 || response.status === 205) return null

  const contentLength = response.headers.get('content-length')
  if (contentLength !== null && /^\d+$/.test(contentLength)) {
    const declaredBytes = Number(contentLength)
    if (
      !Number.isSafeInteger(declaredBytes) ||
      declaredBytes > maxResponseBytes
    ) {
      await cancelBodySafely(response.body)
      throw new ResponseTooLargeError()
    }
  }

  const raw = await readBoundedText(response, maxResponseBytes)
  if (!raw) return null
  if (
    !(response.headers.get('content-type') ?? '').includes('application/json')
  ) {
    return raw
  }

  try {
    return JSON.parse(raw) as unknown
  } catch {
    return raw
  }
}

async function readBoundedText(
  response: Response,
  maxResponseBytes: number
): Promise<string> {
  const reader = response.body?.getReader()
  if (!reader) return ''

  const chunks: Uint8Array[] = []
  let totalBytes = 0

  try {
    while (true) {
      const chunk = await reader.read()
      if (chunk.done) break

      totalBytes += chunk.value.byteLength
      if (totalBytes > maxResponseBytes) {
        try {
          await reader.cancel()
        } catch {
          // The typed size failure remains authoritative.
        }
        throw new ResponseTooLargeError()
      }
      chunks.push(chunk.value)
    }

    const combined = new Uint8Array(totalBytes)
    let offset = 0
    for (const chunk of chunks) {
      combined.set(chunk, offset)
      offset += chunk.byteLength
    }

    return new TextDecoder().decode(combined)
  } finally {
    reader.releaseLock()
  }
}

async function cancelBodySafely(
  body: ReadableStream<Uint8Array> | null
): Promise<void> {
  try {
    await body?.cancel()
  } catch {
    // The typed size failure remains authoritative.
  }
}

function classifyHttpFailure(
  status: number,
  data: unknown
): FetchErrorCategory {
  if (status === 401) return 'authentication_required'
  if (status === 403) return 'authorization_denied'

  const code = getFailureCode(data)
  if (status === 409 && code === 'DUPLICATE_COMMAND') return 'duplicate_command'
  if (status === 409 || status === 422) return 'business_rejection'

  return 'http_error'
}

function getFailureCode(data: unknown): string | undefined {
  const parsed = failureEnvelopeSchema.safeParse(data)
  return parsed.success ? parsed.data.code?.toUpperCase() : undefined
}

function getErrorMessage(data: unknown, fallback: string): string {
  if (typeof data === 'string' && data.trim()) return data

  const parsed = failureEnvelopeSchema.safeParse(data)
  if (parsed.success && parsed.data.message?.trim()) return parsed.data.message
  if (parsed.success && parsed.data.error?.trim()) return parsed.data.error

  return fallback || 'The request failed.'
}

function getErrorDetail(data: unknown, status: number): string {
  const parsed = failureEnvelopeSchema.safeParse(data)
  if (parsed.success && parsed.data.detail?.trim()) return parsed.data.detail
  if (parsed.success && parsed.data.code?.trim()) return parsed.data.code

  return `HTTP_${status}`
}

function getCorrelationId(
  headers: Readonly<Record<string, string>>
): string | undefined {
  const value = Object.entries(headers).find(
    ([key]) => key.toLowerCase() === 'x-request-id'
  )?.[1]

  return normalizeRequestId(value ?? null) ?? undefined
}

function getFullJitterDelay(policy: RequestPolicy, attempt: number): number {
  const ceiling = Math.min(
    policy.maxBackoffMs,
    policy.backoffBaseMs * 2 ** Math.max(0, attempt - 1)
  )

  return Math.floor(Math.random() * (ceiling + 1))
}

function sleepUntil(
  milliseconds: number,
  signal: AbortSignal
): Promise<boolean> {
  if (signal.aborted) return Promise.resolve(false)

  return new Promise((resolve) => {
    const finish = (completed: boolean): void => {
      clearTimeout(timer)
      signal.removeEventListener('abort', handleAbort)
      resolve(completed)
    }
    const handleAbort = (): void => finish(false)
    const timer = setTimeout(() => finish(true), milliseconds)
    signal.addEventListener('abort', handleAbort, { once: true })
  })
}

function emitCompletion(
  emitter: ReturnType<typeof createTelemetryEmitter> | undefined,
  response: FetcherResponse<unknown>,
  method: HttpMethod
): void {
  emitter?.emit('request.completed', {
    durationMs: response.meta.elapsedMs,
    outcome: response.ok ? 'succeeded' : (response.data?.category ?? 'unknown'),
    attributes: {
      attempt: response.meta.attemptCount,
      method,
      policyId: response.meta.policyId,
      status: response.meta.status,
      unknownOutcome: response.ok
        ? false
        : (response.data?.unknownOutcome ?? false),
    },
  })
}

export function stringifyBody<TBody>(
  method: HttpMethod,
  body?: TBody
): string | undefined {
  return method === 'GET' || body === undefined
    ? undefined
    : JSON.stringify(body)
}
